// src/pages/AdminTeams.js
import React, { useEffect, useMemo, useState } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "../firebase";

/** Compose a friendly room label from many possible shapes */
function composeRoomLabel(roomObj, roomId) {
  if (!roomObj) return roomId || "-";
  const meta  = roomObj.meta || {};
  const number = roomObj.n ?? meta.number ?? roomObj.no ?? meta.no;
  const name   = roomObj.name ?? meta.n ?? roomObj.title ?? meta.title;
  const block  = roomObj.block ?? meta.block;
  const label  = roomObj.label ?? meta.label;
  if (label && String(label).trim()) return String(label).trim();

  const parts = [];
  if (number) parts.push(String(number)); // e.g., "A202"
  if (name)   parts.push(String(name));   // e.g., "Networks Lab"
  if (block)  parts.push(String(block));  // e.g., "CSE Block"
  return parts.length ? parts.join(" · ") : (roomId || "-");
}

function TeamCard({ team, roomsMap, volsMap, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  const memberList = useMemo(() => {
    const m = team.m || {};
    // normalize to array and carry noc + mobile
    return Object.entries(m).map(([roll, d]) => ({
      roll,
      name: d.n || roll,
      branch: d.b || "",
      section: d.s || "",
      email: d.e || "",
      mobile: d.m || "",
      noc: !!d.noc,
    }));
  }, [team.m]);

  const roomObj = roomsMap[team.room] || null;
  const roomLabel = composeRoomLabel(roomObj, team.room);
  const addedBy = volsMap[team.by] || null;
  const addedByLabel = addedBy
    ? `${addedBy.name || addedBy.email || team.by}${addedBy.email ? ` (${addedBy.email})` : ""}`
    : (team.by || "-");

  const nocSubmittedCount = memberList.filter(m => m.noc).length;
  const nocPendingCount   = memberList.length - nocSubmittedCount;

  return (
    <div className="border rounded-2xl p-4 bg-white">
      <div className="flex items-start gap-3">
        <button
          className="px-2 py-1 text-xs border rounded-lg hover:bg-gray-50"
          onClick={() => setOpen(v => !v)}
        >
          {open ? "Hide" : "View"}
        </button>

        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="font-semibold">{team.n || "(Untitled team)"}</div>
            <div className="text-xs text-gray-500">({team.id})</div>

            {team.room && (
              <div className="text-xs px-2 py-0.5 rounded-lg border bg-gray-50">
                Room: <span className="font-mono">{roomLabel}</span>
                {roomObj && roomLabel !== team.room ? (
                  <span className="text-gray-400"> · {team.room}</span>
                ) : null}
              </div>
            )}

            {team.tb && (
              <div className="text-xs px-2 py-0.5 rounded-lg border bg-gray-50">
                Table: <span className="font-mono">{String(team.tb)}</span>
              </div>
            )}

            {team.by && (
              <div className="text-xs px-2 py-0.5 rounded-lg border bg-gray-50">
                Added by: <span className="font-mono">{addedByLabel}</span>
              </div>
            )}

            <div className="text-xs px-2 py-0.5 rounded-lg border bg-gray-50">
              NOC: <span className="text-green-700 font-medium">{nocSubmittedCount}</span> ✅ ·{" "}
              <span className="text-amber-700 font-medium">{nocPendingCount}</span> ❌
            </div>
          </div>

          <div className="text-xs text-gray-500 mt-1">
            Created: {team.ts ? new Date(team.ts).toLocaleString() : "-"}
          </div>
        </div>

        <div className="text-xs text-gray-500">{memberList.length} members</div>
      </div>

      {open && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2 pr-4">Roll</th>
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Branch</th>
                <th className="py-2 pr-4">Section</th>
                <th className="py-2 pr-4">Email</th>
                <th className="py-2 pr-4">Mobile</th>
                <th className="py-2 pr-4">NOC</th>
              </tr>
            </thead>
            <tbody>
              {memberList.length === 0 && (
                <tr>
                  <td colSpan="7" className="py-4 text-gray-500">No members in this team.</td>
                </tr>
              )}
              {memberList.map((m) => (
                <tr key={m.roll} className="border-b">
                  <td className="py-2 pr-4 font-mono">{m.roll}</td>
                  <td className="py-2 pr-4">{m.name}</td>
                  <td className="py-2 pr-4">{m.branch || "-"}</td>
                  <td className="py-2 pr-4">{m.section || "-"}</td>
                  <td className="py-2 pr-4">{m.email || "-"}</td>
                  <td className="py-2 pr-4">{m.mobile || "-"}</td>
                  <td className="py-2 pr-4">
                    {m.noc ? (
                      <span className="inline-flex items-center text-green-700">✅ Submitted</span>
                    ) : (
                      <span className="inline-flex items-center text-amber-700">❌ Pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminTeams() {
  const [teams, setTeams] = useState([]);
  const [roomsMap, setRoomsMap] = useState({});
  const [volsMap, setVolsMap] = useState({});
  const [q, setQ] = useState("");

  // Filters / Sort
  const [nocFilter, setNocFilter] = useState("all"); // all | submitted | pending
  const [sortBy, setSortBy] = useState("time_desc"); // time_desc | team | room | noc

  // Subscribe to teams
  useEffect(() => {
    const off = onValue(ref(db, "teams"), (snap) => {
      const v = snap.val() || {};
      const rows = Object.entries(v).map(([id, t]) => ({ id, ...t }));
      rows.sort((a, b) => (b.ts || 0) - (a.ts || 0));
      setTeams(rows);
    });
    return () => off();
  }, []);

  // Subscribe to rooms
  useEffect(() => {
    const off = onValue(ref(db, "rooms"), (snap) => {
      setRoomsMap(snap.val() || {});
    });
    return () => off();
  }, []);

  // Subscribe to volunteers
  useEffect(() => {
    const off = onValue(ref(db, "volunteers"), (snap) => {
      const v = snap.val() || {};
      const map = {};
      Object.entries(v).forEach(([id, o]) => {
        map[id] = { name: o.name || "", email: (o.email || "") };
      });
      setVolsMap(map);
    });
    return () => off();
  }, []);

  // Search across team + members + resolved labels
  const searched = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return teams;

    return teams.filter((t) => {
      const roomObj = roomsMap[t.room] || null;
      const addedBy = volsMap[t.by] || null;

      const inTeam =
        (t.n || "").toLowerCase().includes(term) ||
        (t.id || "").toLowerCase().includes(term) ||
        (t.room || "").toLowerCase().includes(term) ||
        (composeRoomLabel(roomObj, t.room) || "").toLowerCase().includes(term) ||
        String(t.tb || "").toLowerCase().includes(term) ||
        (addedBy?.name || "").toLowerCase().includes(term) ||
        (addedBy?.email || "").toLowerCase().includes(term) ||
        (t.by || "").toLowerCase().includes(term);

      if (inTeam) return true;

      const m = t.m || {};
      return Object.entries(m).some(([roll, d]) => {
        return (
          (roll || "").toLowerCase().includes(term) ||
          (d?.n || "").toLowerCase().includes(term) ||
          (d?.e || "").toLowerCase().includes(term) ||
          (d?.b || "").toLowerCase().includes(term) ||
          (d?.s || "").toLowerCase().includes(term) ||
          (d?.m || "").toLowerCase().includes(term)
        );
      });
    });
  }, [teams, q, roomsMap, volsMap]);

  // Apply NOC filter
  const filtered = useMemo(() => {
    if (nocFilter === "all") return searched;

    return searched.map(team => {
      const m = team.m || {};
      // Filter members by NOC inside each team (so counts & table reflect filter)
      const mFiltered = Object.fromEntries(
        Object.entries(m).filter(([_, d]) => {
          const isSubmitted = !!d?.noc;
          return nocFilter === "submitted" ? isSubmitted : !isSubmitted;
        })
      );
      return { ...team, m: mFiltered };
    }).filter(team => Object.keys(team.m || {}).length > 0);
  }, [searched, nocFilter]);

  // Sort
  const sorted = useMemo(() => {
    const cp = [...filtered];
    switch (sortBy) {
      case "team":
        cp.sort((a, b) => (a.n || "").localeCompare(b.n || "") || (a.id || "").localeCompare(b.id || ""));
        break;
      case "room":
        cp.sort((a, b) => {
          const la = composeRoomLabel(roomsMap[a.room] || null, a.room);
          const lb = composeRoomLabel(roomsMap[b.room] || null, b.room);
          return la.localeCompare(lb) || String(a.tb || "").localeCompare(String(b.tb || ""));
        });
        break;
      case "noc":
        // Pending first (teams with more pending come earlier), then submitted
        const pendingCount = (t) => {
          const m = t.m || {};
          return Object.values(m).reduce((acc, d) => acc + (d?.noc ? 0 : 1), 0);
        };
        cp.sort((a, b) => pendingCount(b) - pendingCount(a));
        break;
      case "time_desc":
      default:
        cp.sort((a, b) => (b.ts || 0) - (a.ts || 0));
    }
    return cp;
  }, [filtered, sortBy, roomsMap]);

  const totals = useMemo(() => {
    const teamsCount = sorted.length;
    let membersCount = 0;
    let submitted = 0;
    sorted.forEach(t => {
      const m = t.m || {};
      const arr = Object.values(m);
      membersCount += arr.length;
      submitted += arr.filter(d => !!d?.noc).length;
    });
    return { teamsCount, membersCount, submitted, pending: membersCount - submitted };
  }, [sorted]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Admin — Teams & Participants</h1>
          <div className="text-sm text-gray-600">
            Total teams: <span className="font-semibold">{totals.teamsCount}</span> •{" "}
            Participants (after filters): <span className="font-semibold">{totals.membersCount}</span> •{" "}
            NOC ✅ <span className="font-semibold text-green-700">{totals.submitted}</span> ·{" "}
            ❌ <span className="font-semibold text-amber-700">{totals.pending}</span>
          </div>
        </div>

        <div className="ml-auto flex flex-wrap gap-2">
          <input
            className="p-3 border rounded-xl w-72"
            placeholder="Search team/roll/name/email/room/table/volunteer/mobile"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

          <select
            className="p-3 border rounded-xl"
            value={nocFilter}
            onChange={(e) => setNocFilter(e.target.value)}
            title="Filter by NOC"
          >
            <option value="all">Show: All</option>
            <option value="submitted">Show: NOC Submitted</option>
            <option value="pending">Show: NOC Pending</option>
          </select>

          <select
            className="p-3 border rounded-xl"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            title="Sort"
          >
            <option value="time_desc">Sort: Time (new → old)</option>
            <option value="team">Sort: Team</option>
            <option value="room">Sort: Room</option>
            <option value="noc">Sort: NOC (pending first)</option>
          </select>
        </div>
      </div>

      <div className="grid gap-4">
        {sorted.map((t, i) => (
          <TeamCard
            key={t.id}
            team={t}
            roomsMap={roomsMap}
            volsMap={volsMap}
            defaultOpen={i < 3}
          />
        ))}
        {sorted.length === 0 && (
          <div className="text-gray-500">No teams match your filters/search.</div>
        )}
      </div>
    </div>
  );
}
